import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Reveal, MaskText, Stagger, StaggerItem } from "./Reveal";

const LESSONS = ["Customers.", "Sales.", "Inventory.", "Cash flow.", "Competition.", "Financing.", "Partnerships.", "Marketing.", "Leadership.", "Setbacks.", "Scale.", "And eventually, letting go."];

export const MeetBrian = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "10%"]);
  const y2 = useTransform(scrollYProgress, [0, 1], ["6%", "-8%"]);

  return (
    <section id="brian" ref={ref} className="relative bg-ocean text-cream py-24 sm:py-32 overflow-hidden">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-y-14 gap-x-12 items-start">
          <div className="lg:col-span-5 relative">
            <figure className="relative grain overflow-hidden border border-white/10">
              <motion.img
                style={{ y }}
                src="/images/brian-speaking-boots.png"
                alt="Brian Smith holding the original sheepskin boots"
                className="w-full h-[56vh] lg:h-[66vh] object-cover object-[center_62%] img-warm scale-105"
              />
              <figcaption className="absolute bottom-0 inset-x-0 z-10 p-5 bg-gradient-to-t from-ocean to-transparent">
                <span className="font-sans text-[11px] uppercase tracking-[0.2em] text-cream/80">
                  Brian today — the boots that started it all
                </span>
              </figcaption>
            </figure>
            <motion.figure
              initial={{ opacity: 0, y: 30, rotate: 6 }}
              whileInView={{ opacity: 1, y: 0, rotate: 3 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
              className="mt-6 sm:mt-0 sm:absolute sm:-bottom-12 sm:-right-8 w-40 sm:w-44 md:w-52 bg-cream p-2 pb-1 shadow-[0_20px_50px_-15px_rgba(0,0,0,0.6)] border border-cream"
            >
              <img src="/images/ugg-vintage-ad.jpeg" alt="Original 1980s UGG Imports magazine advertisement" className="w-full object-cover" />
              <figcaption className="py-2 text-center font-sans text-[9px] uppercase tracking-[0.16em] text-ocean/60">UGG Imports, Inc. · c. 1985</figcaption>
            </motion.figure>
          </div>

          <div className="lg:col-span-7 lg:pl-8">
            <span className="font-sans text-xs uppercase tracking-[0.28em] text-cognac">Brian Smith</span>
            <h2 className="mt-6 font-serif tracking-[-0.02em] leading-[0.96] text-4xl sm:text-5xl lg:text-[4.2rem]">
              <MaskText lines={["This isn't business theory.", <span key="b" className="italic text-cognac">It's what Brian learned</span>, <span key="c" className="italic text-cognac">building one.</span>]} />
            </h2>

            <div className="mt-9 grid sm:grid-cols-2 gap-8 max-w-3xl font-sans text-base sm:text-lg text-cream/75 leading-relaxed">
              <Reveal>
                <p>
                  Brian Smith didn't build UGG from a perfectly executed business plan. He started
                  with an idea, six pairs of boots and a market that largely didn't understand the
                  product. Then he spent years learning what each new stage of growth demanded.
                </p>
              </Reveal>
              <Reveal delay={0.12}>
                <p>
                  <em>Create Your Business Roadmap in 7 Days</em> turns those lessons into a system
                  founders can apply to their own businesses.
                </p>
              </Reveal>
            </div>

            <Stagger className="mt-10 flex flex-wrap gap-x-4 gap-y-2 max-w-2xl" amount={0.05}>
              {LESSONS.map((l) => (
                <StaggerItem key={l}>
                  <span className={`font-serif text-xl sm:text-2xl leading-snug ${l.startsWith("And") ? "italic text-cognac" : "text-cream/85"}`}>{l}</span>
                </StaggerItem>
              ))}
            </Stagger>

            <div className="mt-12 grid sm:grid-cols-12 gap-8 items-center">
              <figure className="sm:col-span-4 relative grain overflow-hidden border border-white/10">
                <motion.img
                  style={{ y: y2 }}
                  src="/images/brian-bookshelf.png"
                  alt="Brian Smith, founder of UGG"
                  className="w-full h-[34vh] object-cover object-top img-warm scale-110"
                />
              </figure>
              <Reveal delay={0.1} className="sm:col-span-8">
                <p className="font-sans text-sm uppercase tracking-[0.2em] text-cream/50">Brian's stories are not presented as</p>
                <p className="mt-2 font-serif text-2xl text-cream/45 line-through decoration-cognac/60">“Do exactly what I did.”</p>
                <p className="mt-5 font-sans text-sm uppercase tracking-[0.2em] text-cognac">They become a way of asking</p>
                <p className="mt-2 font-serif italic text-2xl sm:text-3xl text-cream leading-snug">
                  “What did this stage teach me — and what does your stage require from you?”
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
